<template>
  <div>
    <v-row dense>
      <v-col cols="12">
        <div class="d-flex align-center text-orange-accent-4 mt-3">
          <v-icon icon="mdi-alert" color="orange-accent-4" class="mr-2" />
          <span> Notice: Be sure to save after updating values for it to be reflected. </span>
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="8">
        <v-text-field
          v-model="oidcIdpSearch"
          label="Search"
          append-icon="mdi-magnify"
          variant="underlined"
          color="primary"
        />
      </v-col>
      <v-col cols="auto" class="d-flex justify-end ml-auto">
        <cs-button
          primary
          label="Add OIDC Configuration"
          aria-label="Add OIDC Configuration"
          class="mt-4"
          left-icon="mdi-plus-circle"
          :disabled="saveInProgress || loadInProgress"
          @click="onAddClick"
        />
      </v-col>
    </v-row>

    <v-data-table
      :items="inputFields"
      height="400"
      fixed-header
      :loading="loadInProgress"
      :search="oidcIdpSearch"
      density="compact"
      disable-sort
      :headers="tableHeaders"
    >
      <template #[`item.tenantSubDomain`]="{ item }">
        {{ item.tenantSubDomain || '-' }}
      </template>
      <template #[`item.clientId`]="{ item }">
        {{ item.clientId || '-' }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon
          icon="mdi-pencil"
          color="primary"
          :disabled="saveInProgress"
          class="mr-2"
          @click="onEditRowClick(item)"
        />
        <v-icon
          icon="mdi-delete"
          color="primary"
          :disabled="saveInProgress"
          @click="onDeleteClick(item)"
        />
      </template>
    </v-data-table>
    <v-row dense class="mt-4">
      <v-col cols="12" class="d-flex justify-end">
        <cs-button
          primary
          label="Save"
          aria-label="Save OIDC configurations"
          :loading="saveInProgress"
          :disabled="saveInProgress || loadInProgress"
          @click="onSaveClick()"
        />
      </v-col>
    </v-row>

    <cs-form-dialog
      id="dlg-client-hub-oidc-idp-management-edit"
      v-model="editDialogDisplayed"
      body-id="dlg-client-hub-oidc-idp-management-edit-body"
      :heading="editingIndex !== null ? 'Edit OIDC Configuration' : 'Add OIDC Configuration'"
      :primary-action="{
        label: editingIndex !== null ? 'Update' : 'Add',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="onFormSubmit()"
      @secondary1-click="onFormCancel()"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row dense>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.tenantSubDomain"
                label="Tenant Sub Domain"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.tenantSubDomain"
                @blur="v$.formFields.tenantSubDomain.$touch()"
              />
            </v-col>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.providerName"
                label="Provider Name"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.providerName"
                @blur="v$.formFields.providerName.$touch()"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.clientId"
                label="Client ID"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.clientId"
                @blur="v$.formFields.clientId.$touch()"
              />
            </v-col>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.clientSecret"
                :type="formFields.showClientSecret ? 'text' : 'password'"
                label="Client Secret"
                variant="underlined"
                color="primary"
                :append-inner-icon="formFields.showClientSecret ? 'mdi-eye-off' : 'mdi-eye'"
                :error-messages="formErrors.clientSecret"
                @click:append-inner="formFields.showClientSecret = !formFields.showClientSecret"
                @blur="v$.formFields.clientSecret.$touch()"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.oidcIssuer"
                label="OIDC Issuer"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.oidcIssuer"
                @blur="v$.formFields.oidcIssuer.$touch()"
              />
            </v-col>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.scopes"
                label="Scopes"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.scopes"
                @blur="v$.formFields.scopes.$touch()"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6" class="field-container">
              <v-select
                v-model="formFields.attributeRequestHttpMethod"
                label="HTTP Method"
                variant="underlined"
                color="primary"
                :items="attributeRequestHttpMethodOptions"
                :error-messages="formErrors.attributeRequestHttpMethod"
                @update:model-value="v$.formFields.attributeRequestHttpMethod.$touch()"
              />
            </v-col>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.authorizationEndpoint"
                label="Authorization Endpoint"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.authorizationEndpoint"
                @blur="v$.formFields.authorizationEndpoint.$touch()"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.tokenEndpoint"
                label="Token Endpoint"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.tokenEndpoint"
                @blur="v$.formFields.tokenEndpoint.$touch()"
              />
            </v-col>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.userinfoEndpoint"
                label="Userinfo Endpoint"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.userinfoEndpoint"
                @blur="v$.formFields.userinfoEndpoint.$touch()"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6" class="field-container">
              <v-text-field
                v-model="formFields.jwksUri"
                label="JWKS URI"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.jwksUri"
                @blur="v$.formFields.jwksUri.$touch()"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" class="field-container">
              <v-text-field
                v-model="formFields.attributeMappings"
                label="Attribute Mappings"
                variant="underlined"
                color="primary"
                placeholder="Type hub-attr1:oidc-attr1 | hub-attr2:oidc-attr2 and press Enter"
                :hint="attributeMappingsHint"
                persistent-hint
                :error-messages="formErrors.attributeMappings"
                @blur="v$.formFields.attributeMappings.$touch()"
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-oidc-idp-management-save-success"
      v-model="showSaveSuccessDialog"
      heading="Save Success"
      body-id="dlg-client-hub-oidc-idp-management-save-success-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveSuccessDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <span> The OIDC identity provider information has been saved successfully. </span>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-oidc-idp-management-save-error"
      v-model="showSaveErrorDialog"
      heading="Save Error"
      body-id="dlg-client-hub-oidc-idp-management-save-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>
                  There is some error while saving the OIDC identity provider information. Please
                  try again.
                </p>
                <p class="text-red-accent-4">{{ errorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-oidc-idp-management-validation-error"
      v-model="showValidationErrorDialog"
      heading="Validation Error"
      body-id="dlg-client-hub-oidc-idp-management-validation-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showValidationErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p class="text-red-accent-4">{{ validationErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-oidc-idp-management-load-error"
      v-model="showLoadErrorDialog"
      heading="Load Error"
      body-id="dlg-client-hub-oidc-idp-management-load-error-body"
      :primary-action="{
        label: 'Retry',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Close',
        eventName: 'secondary1-click'
      }"
      @primary-click="fetchOidcIdpInfo()"
      @secondary1-click="showLoadErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>
                  Failed to load the OIDC identity provider configuration. Please check your
                  connection and try again.
                </p>
                <p class="text-red-accent-4">{{ loadErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import { helpers, required, minLength, maxLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import * as types from '../store/mutationTypes';
import { validateProviderName } from '@/components/validation/validateProviderName';
import {
  validateAttributeMappings,
  formatAttributeMappings
} from '@/components/validation/validateAttributeMappings';

const DEFAULT_INPUT_FIELDS = {
  tenantSubDomain: '',
  providerName: '',
  clientId: '',
  clientSecret: '',
  oidcIssuer: '',
  scopes: '',
  attributeRequestHttpMethod: '',
  authorizationEndpoint: '',
  tokenEndpoint: '',
  userinfoEndpoint: '',
  jwksUri: '',
  attributeMappings: '',
  showClientSecret: false
};

export default {
  name: 'ClientHubOidcIdpManagement',

  props: {
    clientSid: { type: String, required: false, default: '' }
  },

  emits: ['save-success'],

  setup: () => ({ v$: useVuelidate() }),

  data() {
    return {
      attributeMappingsHint:
        'Required: email, family_name, given_name, preferred_username. (e.g. preferred_username:upn | email:mail)',
      tableHeaders: [
        { title: 'Tenant Sub Domain', key: 'tenantSubDomain' },
        { title: 'Client ID', key: 'clientId' },
        { title: 'Actions', key: 'actions', align: 'center' }
      ],
      oidcIdpSearch: '',
      editDialogDisplayed: false,
      editingIndex: null,
      inputFields: [],
      formFields: cloneDeep(DEFAULT_INPUT_FIELDS),
      attributeRequestHttpMethodOptions: ['GET', 'POST'],
      saveInProgress: false,
      showSaveSuccessDialog: false,
      showSaveErrorDialog: false,
      errorMessage: '',
      loadInProgress: false,
      showLoadErrorDialog: false,
      loadErrorMessage: '',
      showValidationErrorDialog: false,
      validationErrorMessage: ''
    };
  },

  validations() {
    return {
      formFields: {
        tenantSubDomain: {
          required: helpers.withMessage('Tenant subdomain is required', required),
          minLength: helpers.withMessage(
            'Tenant subdomain must be at least 2 characters',
            minLength(2)
          ),
          maxLength: helpers.withMessage(
            'Tenant subdomain cannot be more than 31 characters',
            maxLength(31)
          )
        },
        providerName: {
          required: helpers.withMessage(
            'Provider Name is required',
            (v) => v !== null && v !== undefined && v !== ''
          ),
          validateProviderName: helpers.withMessage(
            () => {
              const result = validateProviderName({
                inputFields: this.inputFields,
                editingIndex: this.editingIndex,
                tenantSubDomain: this.formFields.tenantSubDomain
              })(this.formFields.providerName);
              return typeof result === 'string' ? result : 'Provider name validation failed';
            },
            (v) =>
              validateProviderName({
                inputFields: this.inputFields,
                editingIndex: this.editingIndex,
                tenantSubDomain: this.formFields.tenantSubDomain
              })(v) === true
          )
        },
        clientId: {
          required: helpers.withMessage('Client ID is required', required)
        },
        clientSecret: {
          required: helpers.withMessage('Client Secret is required', required)
        },
        oidcIssuer: {
          required: helpers.withMessage('OIDC Issuer is required', required)
        },
        scopes: {
          required: helpers.withMessage('Scopes is required', required)
        },
        attributeRequestHttpMethod: {
          required: helpers.withMessage('HTTP method is required', required),
          validValue: helpers.withMessage(
            'Must be GET or POST',
            (v) => !v || ['GET', 'POST'].includes(v)
          )
        },
        authorizationEndpoint: {
          required: helpers.withMessage('Authorization Endpoint is required', required),
          minLength: helpers.withMessage(
            'Authorization Endpoint must be at least 2 characters',
            minLength(2)
          )
        },
        tokenEndpoint: {
          required: helpers.withMessage('Token Endpoint is required', required),
          minLength: helpers.withMessage(
            'Token Endpoint must be at least 2 characters',
            minLength(2)
          )
        },
        userinfoEndpoint: {
          required: helpers.withMessage('Userinfo Endpoint is required', required),
          minLength: helpers.withMessage(
            'Userinfo Endpoint must be at least 2 characters',
            minLength(2)
          )
        },
        jwksUri: {
          required: helpers.withMessage('JWKS URI is required', required),
          minLength: helpers.withMessage('JWKS URI must be at least 2 characters', minLength(2))
        },
        attributeMappings: {
          validateMappings: helpers.withMessage(
            () => {
              const result = validateAttributeMappings(
                this.formFields.attributeMappings,
                'oidc',
                this.attributeMappingsHint
              );
              return typeof result === 'string' ? result : 'Invalid attribute mappings';
            },
            (v) => validateAttributeMappings(v, 'oidc', this.attributeMappingsHint) === true
          )
        }
      }
    };
  },

  computed: {
    formErrors() {
      const errors = {};
      if (!this.v$.formFields) return errors;
      Object.entries(this.v$.formFields).forEach(([key, field]) => {
        if (!field.$error) {
          errors[key] = '';
        } else {
          const error = field.$errors[0];
          errors[key] = error?.$message || error?.$validator || '';
        }
      });
      return errors;
    }
  },

  watch: {
    'formFields.tenantSubDomain'() {
      if (this.v$.formFields.providerName.$dirty) {
        this.v$.formFields.providerName.$touch();
      }
    }
  },

  async mounted() {
    await this.fetchOidcIdpInfo();
  },

  methods: {
    async fetchOidcIdpInfo() {
      if (!this.clientSid) return;
      try {
        this.loadInProgress = true;
        this.showLoadErrorDialog = false;
        this.loadErrorMessage = '';

        const response = await this.$store.dispatch('hub-migration/getCsvExternalIdpOidc', {
          clientSid: this.clientSid
        });

        if (response && response.oidcIdpInfo && response.oidcIdpInfo.length > 0) {
          this.inputFields = response.oidcIdpInfo.map((item) => ({
            tenantSubDomain: item.TenantSubDomain || '',
            providerName: item.ProviderName || '',
            clientId: item.ClientId || '',
            clientSecret: item.ClientSecret || '',
            oidcIssuer: item.OidcIssuer || '',
            scopes: item.Scopes || '',
            attributeRequestHttpMethod: item.AttributeRequestHttpMethod || '',
            authorizationEndpoint: item.AuthorizationEndpoint || '',
            tokenEndpoint: item.TokenEndpoint || '',
            userinfoEndpoint: item.UserinfoEndpoint || '',
            jwksUri: item.JwksUri || '',
            attributeMappings: item.AttributeMappings || '',
            showClientSecret: false
          }));
        } else {
          this.inputFields = [];
        }
      } catch (error) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.loadErrorMessage =
          error?.response?.data?.message || error.message || 'Failed to load OIDC configuration';
        this.showLoadErrorDialog = true;
      } finally {
        this.loadInProgress = false;
      }
    },

    trimField(item, field) {
      if (typeof item[field] === 'string') {
        item[field] = item[field].trim();
      }
    },

    onDeleteClick(item) {
      const index = this.inputFields.indexOf(item);
      if (index === -1) return;
      this.inputFields.splice(index, 1);
    },

    onAddClick() {
      this.editingIndex = null;
      this.formFields = cloneDeep(DEFAULT_INPUT_FIELDS);
      this.v$.$reset();
      this.editDialogDisplayed = true;
    },

    onEditRowClick(item) {
      const index = this.inputFields.indexOf(item);
      if (index === -1) return;
      this.editingIndex = index;
      this.formFields = cloneDeep(this.inputFields[index]);
      this.v$.$reset();
      this.editDialogDisplayed = true;
    },

    async onFormSubmit() {
      const isValid = await this.v$.$validate();
      if (!isValid) return;

      if (this.editingIndex !== null) {
        this.inputFields[this.editingIndex] = cloneDeep(this.formFields);
      } else {
        this.inputFields.push(cloneDeep(this.formFields));
      }
      this.editDialogDisplayed = false;
    },

    onFormCancel() {
      this.v$.$reset();
      this.editDialogDisplayed = false;
    },

    async onSaveClick() {
      if (this.inputFields.length === 0) {
        this.showValidationErrorDialog = true;
        this.validationErrorMessage =
          'At least one OIDC configuration record(s) is required to save.';
        return;
      }

      try {
        if (this.saveInProgress) return;
        this.saveInProgress = true;

        const oidcIdpInfo = cloneDeep(this.inputFields).map(({ showClientSecret, ...item }) => {
          const attributeMappings = formatAttributeMappings(item.attributeMappings);

          return {
            TenantSubDomain: item.tenantSubDomain,
            ProviderName: item.providerName,
            ClientId: item.clientId,
            ClientSecret: item.clientSecret,
            OidcIssuer: item.oidcIssuer,
            Scopes: item.scopes,
            AttributeRequestHttpMethod: item.attributeRequestHttpMethod,
            AuthorizationEndpoint: item.authorizationEndpoint,
            TokenEndpoint: item.tokenEndpoint,
            UserinfoEndpoint: item.userinfoEndpoint,
            JwksUri: item.jwksUri,
            AttributeMappings: attributeMappings
          };
        });

        await this.$store.dispatch('hub-migration/postCsvExternalIdpOidc', {
          clientSid: this.clientSid,
          oidcIdpInfo
        });
        this.editDialogDisplayed = false;
        this.showSaveSuccessDialog = true;
        this.$emit('save-success');
      } catch (error) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.errorMessage = error?.response?.data?.message || error.message;
        this.showSaveErrorDialog = true;
      } finally {
        this.saveInProgress = false;
      }
    }
  }
};
</script>
<style scoped>
.field-container {
  padding: 8px 10px 8px 10px;
}
</style>
